import React from 'react';
import Table from '../../components/ui/Table.jsx';
import Card from '../../components/ui/Card.jsx';
import Input from '../../components/ui/Input.jsx';
import Badge from '../../components/ui/Badge.jsx';
import Button from '../../components/ui/Button.jsx';
import { useApi } from '../../hooks/useApi.js';
import { apiFetch } from '../../api.js';

const emptyForm = {
  name: '',
  position: '',
  visible: true,
  content: ''
};

export default function StaffPages() {
  const [refresh, setRefresh] = React.useState(0);
  const [query, setQuery] = React.useState('');
  const [offset, setOffset] = React.useState(0);
  const limit = 25;
  const path = `/api/pages?limit=${limit}&offset=${offset}&q=${encodeURIComponent(query)}`;
  const { data, loading, error } = useApi(path, [refresh, query, offset]);
  const items = data?.items || [];
  const [form, setForm] = React.useState(emptyForm);
  const [selectedId, setSelectedId] = React.useState(null);
  const [showForm, setShowForm] = React.useState(false);
  const [loadingDetail, setLoadingDetail] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [message, setMessage] = React.useState({ text: '', tone: 'info' });

  const columns = [
    { key: 'name', label: 'Page' },
    { key: 'status', label: 'Visibility' },
    { key: 'position', label: 'Order' },
    { key: 'actions', label: 'Actions' }
  ];

  const filters = (
    <input
      className="input table-search"
      type="search"
      placeholder="Search pages"
      value={query}
      onChange={(event) => {
        setQuery(event.target.value);
        setOffset(0);
      }}
    />
  );

  const pagination = {
    offset,
    limit,
    hasNext: items.length === limit,
    onPageChange: setOffset
  };

  const resetForm = React.useCallback(() => {
    setForm(emptyForm);
    setSelectedId(null);
    setMessage({ text: '', tone: 'info' });
  }, []);

  const handleNew = () => {
    const nextPosition = items.length > 0
      ? Math.max(...items.map((item) => Number(item.position) || 0)) + 1
      : 1;
    resetForm();
    setForm((prev) => ({ ...prev, position: String(nextPosition), visible: true }));
    setShowForm(true);
  };

  const handleEdit = async (id) => {
    setLoadingDetail(true);
    setMessage({ text: '', tone: 'info' });
    setShowForm(true);
    setSelectedId(id);
    try {
      const page = await apiFetch(`/api/pages/${id}`);
      setForm({
        name: page.name || '',
        position: page.position ? String(page.position) : '',
        visible: !!page.visible,
        content: page.content || ''
      });
    } catch (err) {
      setMessage({ text: err?.message || 'Failed to load page.', tone: 'error' });
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleToggleVisibility = async (page) => {
    setSaving(true);
    setMessage({ text: '', tone: 'info' });
    try {
      await apiFetch(`/api/pages/${page.id}`, {
        method: 'PUT',
        body: JSON.stringify({ visible: !page.visible })
      });
      setRefresh((value) => value + 1);
    } catch (err) {
      setMessage({ text: err?.message || 'Failed to update visibility.', tone: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this page? This cannot be undone.')) return;
    setSaving(true);
    setMessage({ text: '', tone: 'info' });
    try {
      await apiFetch(`/api/pages/${id}`, { method: 'DELETE' });
      setRefresh((value) => value + 1);
      if (selectedId === id) {
        resetForm();
        setShowForm(false);
      }
    } catch (err) {
      setMessage({ text: err?.message || 'Failed to delete page.', tone: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const updateField = (key) => (event) => {
    const value = key === 'visible' ? event.target.checked : event.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const validate = () => {
    if (!form.name.trim()) return 'Page name is required.';
    return '';
  };

  const handleSave = async () => {
    const validation = validate();
    if (validation) {
      setMessage({ text: validation, tone: 'error' });
      return;
    }
    setSaving(true);
    setMessage({ text: '', tone: 'info' });
    try {
      const position = parseInt(form.position, 10);
      const payload = {
        name: form.name.trim(),
        visible: !!form.visible,
        content: form.content || ''
      };
      if (Number.isFinite(position)) {
        payload.position = position;
      }

      if (selectedId) {
        await apiFetch(`/api/pages/${selectedId}`, {
          method: 'PUT',
          body: JSON.stringify(payload)
        });
        setMessage({ text: 'Page updated.', tone: 'info' });
      } else {
        await apiFetch('/api/pages', {
          method: 'POST',
          body: JSON.stringify(payload)
        });
        resetForm();
        setShowForm(false);
        setMessage({ text: 'Page created.', tone: 'info' });
      }
      setRefresh((value) => value + 1);
    } catch (err) {
      setMessage({ text: err?.message || 'Failed to save page.', tone: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const tableData = items.map((page) => ({
    name: page.name,
    status: page.visible ? <Badge tone="success">Visible</Badge> : <Badge tone="warning">Hidden</Badge>,
    position: page.position,
    actions: (
      <div className="table-actions">
        <Button variant="ghost" type="button" onClick={() => handleEdit(page.id)}>Edit</Button>
        <Button variant="ghost" type="button" onClick={() => handleToggleVisibility(page)}>Toggle</Button>
        <Button variant="ghost" type="button" onClick={() => handleDelete(page.id)}>Delete</Button>
      </div>
    )
  }));

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>CMS Pages</h1>
          <p className="subtle">Manage public content and page order.</p>
        </div>
        <div className="header-actions">
          {showForm ? (
            <Button variant="secondary" type="button" onClick={() => setShowForm(false)}>Hide Form</Button>
          ) : (
            <Button type="button" onClick={handleNew}>New Page</Button>
          )}
        </div>
      </div>

      {message.text ? <div className={message.tone === 'error' ? 'alert error' : 'alert'}>{message.text}</div> : null}

      {showForm ? (
        <Card title={selectedId ? 'Edit Page' : 'New Page'}>
          {loadingDetail ? (
            <p className="subtle">Loading page...</p>
          ) : (
            <>
              <div className="form-grid">
                <Input label="Page Name" value={form.name} onChange={updateField('name')} placeholder="Student Forms" />
                <Input label="Order" value={form.position} onChange={updateField('position')} placeholder="1" />
                <div className="input-field">
                  <span className="input-label">Visibility</span>
                  <label className="switch">
                    <input type="checkbox" checked={form.visible} onChange={updateField('visible')} />
                    <span className="slider" />
                  </label>
                  <span className="input-helper">Toggle whether the page appears in the student navigation.</span>
                </div>
              </div>
              <label className="input-field">
                <span className="input-label">Content</span>
                <textarea
                  className="input"
                  rows="8"
                  value={form.content}
                  onChange={updateField('content')}
                  placeholder="Basic HTML only: p, br, strong, em, ul, ol, li, h1-h6, blockquote"
                />
              </label>
              <div className="form-actions">
                <Button variant="secondary" type="button" onClick={() => { resetForm(); setShowForm(false); }}>Cancel</Button>
                <Button type="button" disabled={saving} onClick={handleSave}>{saving ? 'Saving...' : 'Save Page'}</Button>
              </div>
            </>
          )}
        </Card>
      ) : null}

      {error ? <div className="alert error">{error}</div> : null}
      {loading ? (
        <p className="subtle">Loading...</p>
      ) : (
        <Table
          columns={columns}
          data={tableData}
          filters={filters}
          pagination={pagination}
          emptyMessage="No pages found."
        />
      )}
    </div>
  );
}
